import { ComponentPropsContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { CartContextProvider } from 'components/globalContext/CartContext';
import { Auth } from 'components/helpers/Auth';
import Button from 'components/helpers/Button';
import PageLayout from 'components/layout/PageLayout';
import { FastSitecoreContext } from 'lib/sitecore/fast-sitecore-context';
import { SitecorePageProps } from 'lib/sitecore/page-props';
import { sitecorePagePropsFactory } from 'lib/sitecore/page-props-factory';
import { DictionaryContext } from 'lib/sitecore/use-dictionary';
import { logWarning } from 'lib/util/errors';
import { normalizeRedirectUrl } from 'lib/util/urls';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { getSession, SessionProvider, signIn, SignInOptions, useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { componentFactory } from 'temp/component-factory';

const WARN_SOURCE = 'OKTA_LOGIN';

interface SigninPageProps extends SitecorePageProps {
  autoProcess: boolean;
}

/**
 * Creates a callback-appropraite SignInOptions object.
 * @param callbackUrl Callback URL to redirect to.
 */
export const getSignInOptions = (callbackUrl: string | undefined): SignInOptions | undefined => {
  let signInOptions: SignInOptions | undefined;
  if (callbackUrl) {
    signInOptions = {
      callbackUrl,
    };
  }
  return signInOptions;
};

/**
 * Checks to see if a request can be automatically processed.
 * @param error Error query string.
 */
export const shouldAutoProcess = (error: string | string[] | undefined): boolean => {
  if (!error) {
    return true;
  }
  if (Array.isArray(error)) {
    return error.every(shouldAutoProcess);
  }
  return error.toLocaleLowerCase() === 'sessionrequired';
};

function SignInImpl({ callbackUrl }: Readonly<{ callbackUrl: string | undefined }>) {
  const { data: session } = useSession();

  // Used to keep track of whether we already triggered a signin so it doesn't get called multiple times.
  const signedInRef = useRef(false);

  // Run client-side on page load.  This needs to run click-side bcause it is a POST request.
  useEffect(() => {
    // If there's no session, sign in to Okta
    if (signedInRef.current === false && !session) {
      signedInRef.current = true;
      const signInOptions = getSignInOptions(callbackUrl);
      signIn('okta', signInOptions);
    }
  });

  // We don't want to display any content
  return <></>;
}

function modal(callbackUrl: string | undefined) {
  return (
    <div className="absolute left-1/2 top-1/3 z-50 -translate-x-1/2 -translate-y-1/2">
      <div className="grid grid-flow-col grid-rows-2 justify-items-center rounded-lg border-0 bg-white p-7 shadow-lg outline-none focus:outline-none">
        <div className="text-lg">
          <h3 className="">Please sign in to continue.</h3>
        </div>
        <div className="">
          <Button
            onClick={() => {
              const signInOptions = getSignInOptions(callbackUrl);
              signIn('okta', signInOptions);
            }}
          >
            Sign In
          </Button>
        </div>
      </div>
    </div>
  );
}

const SignIn: NextPage<SigninPageProps> = ({
  componentProps,
  layoutData,
  dictionary,
  autoProcess,
}) => {
  const router = useRouter();
  let callbackUrl: string | undefined;
  // Check callback url parameter
  if (Array.isArray(router.query.callbackUrl)) {
    logWarning(
      {
        source: WARN_SOURCE,
        message: 'Multiple callback url parameters detected.',
      },
      router.query.callbackUrl
    );
    callbackUrl = router.query.callbackUrl[0];
  } else {
    callbackUrl = router.query.callbackUrl;
  }

  if (autoProcess) {
    return (
      <SessionProvider>
        <SignInImpl callbackUrl={callbackUrl} />
      </SessionProvider>
    );
  }

  return (
    <ComponentPropsContext value={componentProps}>
      <FastSitecoreContext layoutData={layoutData} componentFactory={componentFactory}>
        <DictionaryContext.Provider value={dictionary}>
          <Auth skipAuthCheck>
            {modal(callbackUrl)}
            <CartContextProvider>
              <PageLayout isSearchRobot={false} />
            </CartContextProvider>
          </Auth>
        </DictionaryContext.Provider>
      </FastSitecoreContext>
    </ComponentPropsContext>
  );
};

export default SignIn;

const stripHeaderComponent = (props: SigninPageProps): SigninPageProps => {
  const { route } = props?.layoutData?.sitecore ?? { placeholders: {} };
  if (route?.placeholders['nmdp-header']?.length) {
    if ('fields' in route.placeholders['nmdp-header'][0]) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (route.placeholders['nmdp-header'][0].fields as any).root.children = [];
    }
  }
  return props;
};

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<SigninPageProps | { autoProcess: true }>> {
  const session = await getSession(context);
  // If we're logged in and we have a callback url (e.g. user logged in and was sent back to this page)
  if (session) {
    // Normalize user input to prevent blind redirects
    const callbackUrl = normalizeRedirectUrl(context.query.callbackUrl);

    // redirect to that callback url
    return {
      redirect: {
        destination: callbackUrl,
        permanent: false,
      },
    };
  }

  const autoProcess = shouldAutoProcess(context.query.error);
  if (autoProcess) {
    return {
      props: {
        autoProcess,
      },
    };
  }
  logWarning(
    {
      source: WARN_SOURCE,
      message: `Unexpected login flow detected.  Flow error details: `,
    },
    context.query.error,
    context
  );
  // Load up the login UI page from sitecore
  const pageData = await sitecorePagePropsFactory.create({
    params: { ...context.params, path: '/_login' },
  });

  const props = stripHeaderComponent({ ...pageData, autoProcess: false });
  return {
    props,
  };
}
